import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatMenuModule } from "@angular/material/menu";
import { TranslocoModule } from "@ngneat/transloco";
import { animate, state, style, transition, trigger } from "@angular/animations";
import { ToastrService } from "ngx-toastr";
import { isHostDevOrLocal, TOAST_FASTER } from "../../common";
import { filter, take } from "rxjs/operators";
import { Actions, ofType } from "@ngrx/effects";
import { DialogLocalComponent } from "../dialog-local/dialog-local.component";
import { StoreSelectorsService } from "../../services/store-selectors.service";
import { actionFeedMeAnyRequest, actionFeedMeAnySuccess, actionFeedMeAnyFailure } from "../../store/actions/user.actions";

@Component({
    selector: "app-dialog-any-feed",
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatMenuModule,
        TranslocoModule,
    ],
    templateUrl: "./dialog-any-feed.component.html",
    styleUrls: ["./dialog-any-feed.component.scss"],
    animations: [
        trigger("fadeInOut", [
            state("in", style({
                opacity: 1,
            })),
            state("out", style({
                opacity: 0,
            })),
            transition("in => out", animate("400ms ease-out")),
            transition("out => in", animate("400ms ease-in")),
        ]),
    ],
})
export class DialogAnyFeedComponent implements OnInit, OnDestroy {
    // Wizard mode support
    @Input() wizardMode = false; // When true, component is embedded in wizard (not a dialog)
    @Output() feedCreated = new EventEmitter<any>(); // Emits when feed is created in wizard mode
    @Output() cancelled = new EventEmitter<void>(); // Emits when cancelled in wizard mode

    userQuestion = "";
    isInputFocused = false;
    currentSampleQuestion = "";
    fadeState = "out";
    isLoading = false;
    feedResults: any[] = [];
    errorMessage = "";
    showResults = false;
    debug;
    private sampleQuestionIndex = 0;
    private animationInterval: any;
    selectedLanguage = "en"; // Default to English (language code)
    selectedLanguageName = "English"; // Display name (proper format matching Languages array)
    isVertical = true; // Default to vertical direction
    feedUrl: string | null = null; // Feed URL returned from web service

    sampleQuestions = [
        "What are the best Hollywood films of this year?",
        "Get me the hottest entertainment news for this month",
        "What are the biggest astronomy discoveries of the past 3 years?",
        "Show me the top tech innovations from the last 6 months",
        "What are the most viral social media trends this week?",
        "Find the biggest sports moments of this decade",
        "What are the latest climate change developments from this year?",
        "Show me the top music albums released this month",
        "What are the most important scientific breakthroughs of the past year?",
        "Get me the trending fashion styles for this season",
        "What are the biggest political events from the last 2 years?",
        "Show me the most popular video games released this year",
        "What are the top travel destinations for this summer?",
        "Find the most inspiring success stories from this month",
        "What are the biggest tech company announcements this quarter?",
        "Show me the most watched TV series of this year",
        "What are the top medical advancements from the past 5 years?",
        "Get me the most influential books published this year",
        "What are the biggest environmental initiatives launched this month?",
        "Show me the top startup funding rounds from this quarter",
    ];

    constructor(
        @Optional() public dialogRef: MatDialogRef<DialogAnyFeedComponent>,
        private dialog: MatDialog,
        private toastr: ToastrService,
        private storeSelector: StoreSelectorsService,
        private actions$: Actions,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
    }

    ngOnInit(): void {
        this.debug = isHostDevOrLocal();

        // Set up action listeners for feed request success/failure
        this.actions$.pipe(
            ofType(actionFeedMeAnySuccess),
            filter(() => this.isLoading), // Only handle if we're currently loading
        ).subscribe((action: any) => {
            this.isLoading = false;
            const response = action.response;

            // Parse the JSON response - items have text1 and text2 properties
            if (response && response.success && response.items && Array.isArray(response.items)) {
                this.feedResults = response.items.map((item: any, index: number) => ({
                    number: index + 1,
                    title: item.text1 || "No title",
                    description: item.text2 || "No description",
                }));

                // For now, we'll generate a feedUrl based on the prompt
                // TODO: Backend should return the actual feed URL

                this.feedUrl = `https://ai.signage.me${isHostDevOrLocal() ? ':9443': ''}/feed/${response.prompt}`;
                this.showResults = true;

                // Show success message
                this.toastr.success(
                    `Found ${this.feedResults.length} results`,
                    "Success",
                    TOAST_FASTER,
                );
            } else {
                this.errorMessage = "Invalid response format from server";
                this.toastr.error(this.errorMessage, "Error", TOAST_FASTER);
            }
        });

        // Listen for failure responses
        this.actions$.pipe(
            ofType(actionFeedMeAnyFailure),
            filter(() => this.isLoading), // Only handle if we're currently loading
        ).subscribe((action: any) => {
            this.isLoading = false;
            this.errorMessage = action.errorMessage || "Failed to generate feed";
            this.toastr.error(this.errorMessage, "Error", TOAST_FASTER);
        });

        // Check if direction was passed from parent component
        if (this.data && this.data.isVertical !== undefined) {
            this.isVertical = this.data.isVertical;
        }

        // Check if language was passed from parent component
        if (this.data && this.data.language && this.data.language.trim().length > 0) {
            // Set the language name (e.g., 'English', 'Hebrew', 'Spanish')
            this.selectedLanguageName = this.data.language;

            // Convert language name to language code (e.g., 'English' -> 'en', 'Hebrew' -> 'he')
            // Note: The Languages array from common.ts maps language names to codes
            // We'll use a simple mapping for common languages
            const languageMap: { [key: string]: string } = {
                'English': 'en',
                'Spanish': 'es',
                'French': 'fr',
                'German': 'de',
                'Italian': 'it',
                'Portuguese': 'pt',
                'Russian': 'ru',
                'Japanese': 'ja',
                'Korean': 'ko',
                'Chinese': 'zh',
                'Arabic': 'ar',
                'Hebrew': 'he',
                'Hindi': 'hi',
                'Turkish': 'tr',
                'Dutch': 'nl',
                'Polish': 'pl',
                'Swedish': 'sv',
                'Norwegian': 'no',
                'Danish': 'da',
                'Finnish': 'fi'
            };

            this.selectedLanguage = languageMap[this.data.language] || 'en';
        }

        // Check if prompt was passed from parent component
        if (this.data && this.data.prompt && this.data.prompt.trim().length > 0) {
            // Populate the textarea with the passed prompt
            this.userQuestion = this.data.prompt;
            // Don't start the sample questions animation if we have a prompt
        } else {
            // Start the sample questions animation only if no prompt was provided
            this.shuffleSampleQuestions();
            this.startSampleQuestionsAnimation();
        }
    }

    ngOnDestroy(): void {
        this.stopSampleQuestionsAnimation();
    }

    shuffleSampleQuestions(): void {
        // Fisher-Yates shuffle algorithm
        for (let i = this.sampleQuestions.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.sampleQuestions[i], this.sampleQuestions[j]] = [this.sampleQuestions[j], this.sampleQuestions[i]];
        }
    }

    startSampleQuestionsAnimation(): void {
        this.currentSampleQuestion = this.sampleQuestions[0];
        this.sampleQuestionIndex = 0;
        this.fadeState = "out";

        // Wait 1 second before showing the first question
        setTimeout(() => {
            this.fadeState = "in";

            // Start the cycling animation after the initial fade-in
            this.animationInterval = setInterval(() => {
                // Fade out
                this.fadeState = "out";

                // Wait for fade out to complete, then change question and fade in
                setTimeout(() => {
                    this.sampleQuestionIndex = (this.sampleQuestionIndex + 1) % this.sampleQuestions.length;
                    this.currentSampleQuestion = this.sampleQuestions[this.sampleQuestionIndex];
                    this.fadeState = "in";
                }, 400); // Match the fade-out animation duration
            }, 3000); // Complete cycle every 3 seconds (includes fade out + fade in time)
        }, 1000); // Wait 1 second before first fade-in
    }

    stopSampleQuestionsAnimation(): void {
        if (this.animationInterval) {
            clearInterval(this.animationInterval);
            this.animationInterval = null;
        }
    }

    onInputFocus(event: any): void {
        this.isInputFocused = true;
        this.stopSampleQuestionsAnimation();

        // If userQuestion is empty, populate it with the current sample question
        if (!this.userQuestion && this.currentSampleQuestion) {
            this.userQuestion = this.currentSampleQuestion;

            // Use setTimeout to ensure the value is set before selecting
            setTimeout(() => {
                if (event && event.target) {
                    event.target.select();
                }
            }, 0);
        }
    }

    onInputBlur(): void {
        this.isInputFocused = false;
        if (!this.userQuestion) {
            this.shuffleSampleQuestions();
            this.startSampleQuestionsAnimation();
        }
    }

    onSubmit(): void {
        if (this.userQuestion && this.userQuestion.trim().length > 0) {
            this.isLoading = true;
            this.errorMessage = "";
            this.feedResults = [];
            this.showResults = false;
            this.feedUrl = null;

            // Clean and normalize prompt for better cache hit rates
            const cleanedPrompt = this.cleanPrompt(this.userQuestion);

            // Build fields parameter to match API interface
            const fields = {
                text1: "title",
                text2: "description"
            };

            if (this.debug) {
                console.log("Dispatching feed request:", {
                    prompt: cleanedPrompt,
                    language: this.selectedLanguage,
                    fields: fields,
                });
            }

            // Update userQuestion to cleaned prompt for consistency
            this.userQuestion = cleanedPrompt;

            // Dispatch action to request feed
            this.storeSelector.dispatch(actionFeedMeAnyRequest({
                prompt: cleanedPrompt,
                language: this.selectedLanguage,
                fields: fields
            }));
        }
    }

    /**
     * Open language selector dialog
     */
    openLanguageSelector(): void {
        const dialogRef = this.dialog.open(DialogLocalComponent, {
            height: "700px",
            width: "500px",
            data: {
                selectionOnly: true, // Don't change app language, just select for feed
                currentLanguage: this.selectedLanguage,
            },
            panelClass: "modern-dialog",
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
            if (result && result.code) {
                this.selectedLanguage = result.code;
                this.selectedLanguageName = result.name;  // Keep original format (e.g., 'English', 'Spanish')
            }
        });
    }

    /**
     * Clean and normalize prompt for better cache hit rates
     * - Convert to lowercase
     * - Remove carriage returns and newlines
     * - Trim leading and trailing whitespace
     * - Remove special characters (!, ?, ., ,, etc.)
     * - Replace multiple spaces with single space
     * - Remove extra whitespace
     */
    private cleanPrompt(prompt: string): string {
        return prompt
            .toLowerCase()                          // Convert to lowercase
            .replace(/[\r\n]/g, "")                 // Remove carriage returns and newlines
            .trim()                                 // Trim leading/trailing whitespace
            .replace(/[?!.,;:'"()\[\]{}]/g, "")    // Remove special characters
            .replace(/\s+/g, " ")                   // Replace multiple spaces with single space
            .trim();                                // Final trim after space normalization
    }

    onNewSearch(): void {
        this.userQuestion = "";
        this.feedResults = [];
        this.errorMessage = "";
        this.showResults = false;
        this.shuffleSampleQuestions();
        this.startSampleQuestionsAnimation();
    }

    onApplyFeed(): void {
        // Return the feed URL from web service response
        if (this.debug) {
            console.log("Apply Feed - Question:", this.userQuestion);
            console.log("Selected Language:", this.selectedLanguage);
            console.log("Selected Language Name:", this.selectedLanguageName);
            console.log("Direction (isVertical):", this.isVertical);
            console.log("Feed URL:", this.feedUrl);
            console.log("Feed Results:", this.feedResults);
        }

        const feedData = {
            applied: true,
            results: this.feedResults,
            question: this.userQuestion,
            prompt: this.userQuestion,  // The cleaned/normalized prompt
            language: this.selectedLanguage,  // Language code (e.g., "en", "es", "fr")
            languageName: this.selectedLanguageName,  // Language display name (e.g., "english", "spanish")
            feedUrl: this.feedUrl,  // Feed URL from web service response
        };

        // Wizard mode: emit event instead of closing dialog
        if (this.wizardMode) {
            this.feedCreated.emit(feedData);
        } else {
            // Dialog mode: close dialog and return data
            this.dialogRef?.close(feedData);
        }
    }

    onCancel(): void {
        // Wizard mode: emit cancel event
        if (this.wizardMode) {
            this.cancelled.emit();
        } else {
            // Dialog mode: close dialog
            this.dialogRef?.close();
        }
    }
}
